import * as capitalize from 'lodash/capitalize'
import { SimpleGuard, WatchingGuard } from 'interfaces/guards'
import { ActivePeergroupsTyped } from 'interfaces/peergroups'
import { TimeframeTyped } from 'interfaces/global'
import { MoneyflowTyped, MoneyflowType, MoneyflowTrendOwnProps, MoneyflowBreakdownOwnProps } from 'interfaces/moneyflow'
import {
  isMemberMoneyflowBreakdownLoadedSelector,
  isPeergroupMoneyflowBreakdownLoadedSelector,
  isPeergroupMoneyflowTrendsLoadedSelector,
  hasMemberMoneyflowTrendsSelector,
  isMemberOppositeTotalMoneyflowTrendLoadedSelector,
  hasMoneyflowTrackersSelector,
} from 'selectors/moneyflowSelectors'
import { yourPeergroupIdSelector, selectedPeergroupIdSelector } from 'selectors/peergroupSelectors'
import {
  getMoneyflowTrackersActionCreator,
  getMemberMoneyflowBreakdownActionCreator,
  getPeergroupMoneyflowBreakdownActionCreator,
  getMemberMoneyflowTrendsActionCreator,
  getPeergroupMoneyflowTrendsActionCreator,
  getTransactionsActionCreator,
  getRecentTransactionsActionCreator,
} from 'actions/moneyflowActions'
import { ALL_CATEGORY_ID } from 'constants/meta'
import { TransactionsOwnProps } from 'components/features/pfm/TransactionsModule/TransactionsModule'
import { createGuardOrderError } from 'helpers/errorHelpers'
import * as isNumber from 'lodash/isNumber'

// ===========================================================================//
//                                 BREAKDOWNS                                 //
// ===========================================================================//

export const memberMoneyflowBreakdownGuard: SimpleGuard<MoneyflowBreakdownOwnProps> = {
  name: 'Member Moneyflow Breakdown',
  checker: isMemberMoneyflowBreakdownLoadedSelector,
  loader: (dispatch, { type, timeframe }) =>
    dispatch(getMemberMoneyflowBreakdownActionCreator(type, timeframe)),
}

export const createMemberMoneyflowBreakdownGuard = (type: MoneyflowType): SimpleGuard<TimeframeTyped> => ({
  name: `Member ${ capitalize(type) } Breakdown`,
  checker: (state, { timeframe }) =>
    isMemberMoneyflowBreakdownLoadedSelector(state, { type, timeframe }),
  loader: (dispatch, { timeframe }) =>
    dispatch(getMemberMoneyflowBreakdownActionCreator(type, timeframe)),
})

type PeergroupMoneyflowBreakdownWatch = MoneyflowBreakdownOwnProps & ActivePeergroupsTyped
export const peergroupsMoneyflowBreakdownGuard: WatchingGuard<MoneyflowBreakdownOwnProps, PeergroupMoneyflowBreakdownWatch> = {
  name: 'Peer Group Moneyflow Breakdown',
  checker: isPeergroupMoneyflowBreakdownLoadedSelector,
  watcher: (state, ownProps) => {
    const peersId = yourPeergroupIdSelector(state)
    const othersId = selectedPeergroupIdSelector(state)

    return {
      ...ownProps,
      peersId,
      othersId,
    }
  },
  loader: (dispatch, { type, timeframe, peersId, othersId }) => {
    if (!isNumber(peersId) || !isNumber(othersId)) {
      throw createGuardOrderError('peergroupsMoneyflowBreakdownGuard', 'peergroupsGuard')
    }

    dispatch(getPeergroupMoneyflowBreakdownActionCreator(type, timeframe, [peersId, othersId]))
  },
}

export const createPeergroupMoneyflowBreakdownGuard = (type: MoneyflowType): WatchingGuard<TimeframeTyped, TimeframeTyped & ActivePeergroupsTyped> => ({
  name: `Peer Group ${ capitalize(type) } Breakdown`,
  checker: (state, { timeframe }) =>
    isPeergroupMoneyflowBreakdownLoadedSelector(state, { type, timeframe }),
  watcher: (state, ownProps) => {
    const peersId = yourPeergroupIdSelector(state)
    const othersId = selectedPeergroupIdSelector(state)

    return {
      ...ownProps,
      peersId,
      othersId,
    }
  },
  loader: (dispatch, { timeframe, peersId, othersId }) => {
    if (!isNumber(peersId) || !isNumber(othersId)) {
      throw createGuardOrderError('createPeergroupMoneyflowBreakdownGuard', 'peergroupsGuard')
    }

    dispatch(getPeergroupMoneyflowBreakdownActionCreator(type, timeframe, [peersId, othersId]))
  },
})

// ===========================================================================//
//                                   TRENDS                                   //
// ===========================================================================//

export const memberMoneyflowTrendGuard: SimpleGuard<MoneyflowTrendOwnProps> = {
  name: `Member Moneyflow Trend`,
  checker: hasMemberMoneyflowTrendsSelector,
  loader: (dispatch, { type, timeframe, categoryId }) =>
    dispatch(getMemberMoneyflowTrendsActionCreator(type, timeframe, categoryId)),
}

export const memberOppositeMoneyflowTrendsGuard: SimpleGuard<MoneyflowTrendOwnProps> = {
  name: `Member Moneyflow Trend`,
  checker: isMemberOppositeTotalMoneyflowTrendLoadedSelector,
  loader: (dispatch, { type, timeframe }) => {
    const oppositeType = type === 'spending' ? 'income' : 'spending'
    dispatch(getMemberMoneyflowTrendsActionCreator(oppositeType, timeframe, ALL_CATEGORY_ID))
  },
}

type PeergroupMoneyflowTrendsWatch = MoneyflowTrendOwnProps & ActivePeergroupsTyped
export const peergroupMoneyflowTrendsGuard: WatchingGuard<MoneyflowTrendOwnProps, PeergroupMoneyflowTrendsWatch> = {
  name: `Peer Group Moneyflow Trend`,
  checker: isPeergroupMoneyflowTrendsLoadedSelector,
  watcher: (state, ownProps) => {
    const peersId = yourPeergroupIdSelector(state)
    const othersId = selectedPeergroupIdSelector(state)

    return {
      ...ownProps,
      peersId,
      othersId,
    }
  },
  loader: (dispatch, { type, timeframe, categoryId, peersId, othersId }) => {
    if (!isNumber(peersId) || !isNumber(othersId)) {
      throw createGuardOrderError('createPeergroupMoneyflowBreakdownGuard', 'peergroupsGuard')
    }

    dispatch(getPeergroupMoneyflowTrendsActionCreator(type, timeframe, categoryId, [peersId, othersId]))
  },
}

// ===========================================================================//
//                                  TRACKERS                                  //
// ===========================================================================//

export const moneyflowTrackersGuard: SimpleGuard<MoneyflowTyped> = {
  name: `Moneyflow Trackers`,
  checker: hasMoneyflowTrackersSelector,
  loader: (dispatch, { type }) =>
    dispatch(getMoneyflowTrackersActionCreator.request({ type })),
}

// ===========================================================================//
//                                TRANSACTIONS                                //
// ===========================================================================//

export const transactionsGuard: SimpleGuard<TransactionsOwnProps> = {
  name: `Transactions`,
  checker: (state, { type }) =>
    Boolean(state.data[type].transactions),
  loader: (dispatch, { type, defaultSorting }) =>
    dispatch(getTransactionsActionCreator({ type, sorting: defaultSorting })),
}

export const recentTransactionsGuard: SimpleGuard<{}> = {
  name: `RecentTransactions`,
  checker: (state) => Boolean(state.data.recentTransactions.recent.payload),
  loader: (dispatch) => dispatch(getRecentTransactionsActionCreator.request({})),
}
